import type { Pathname, RouteId } from "$app/types";
import type { PlaceLocation } from "$lib/features/_other/placeLocation";
import { defaultCityFilters, type CityFilters } from "$lib/features/city/cityFilters.type";
import { CITY_FEATURE_PAGE_PER_ITEM_TYPES, CITY_FEATURE_TYPE_ROUTE_PATHS, CityFeatureType } from "$lib/features/cityFeature/cityFeatureType.enum";
import type { CoverImage } from "$lib/features/image/imageDetails.type";
import { FULL_SITE_URL, IMAGE_BASE_URL } from "$lib/statics";

type CityLocationTarget =
    | { location: PlaceLocation; countrySlug?: never; citySlug?: never }
    | { location?: never; countrySlug: string; citySlug: string };

export interface NavLink {
    href: Pathname;
    routeId: RouteId; // matches "page.route.id"
    preloadData?: PreloadData;
    preloadCode?: PreloadCode;
}

export type PreloadData = 'hover' | 'tap' | 'false';
export type PreloadCode = 'hover' | 'tap' | 'viewport' | 'eager' | 'false';

export class UrlUtil {

    public static toHomepage(): NavLink {
        return { href: ``, routeId: '/' };
    }

    public static toCountry({ countrySlug }: { countrySlug: string }): NavLink {
        return { href: `${countrySlug}`, routeId: '/[countrySlug]' };
    }

    public static toCity(params: CityLocationTarget & { section?: string }): NavLink {
        const [countrySlug, citySlug] = this.resolveSlugs(params);
        const hash = params.section ? `#${params.section}` : '';
        return {
            href: `${countrySlug}/${citySlug}${hash}`,
            routeId: '/[countrySlug]/[citySlug]'
        };
    }

    public static toCityFeatures({ type, ...target }: CityLocationTarget & { type: CityFeatureType }): NavLink {
        if (type === CityFeatureType.CITY_DETAILS) return UrlUtil.toCity(target);

        const [countrySlug, citySlug] = this.resolveSlugs(target);
        const typeSlug = CITY_FEATURE_TYPE_ROUTE_PATHS[type];
        return {
            href: `${countrySlug}/${citySlug}/${typeSlug}`,
            routeId: `/[countrySlug]/[citySlug]/[${typeSlug}]`
        };
    }

    public static toCityFeature({ type, featureSlug, ...target }: CityLocationTarget & { type: CityFeatureType; featureSlug: string; section?: string }): NavLink {
        if (type === CityFeatureType.CITY_DETAILS) return UrlUtil.toCity(target);

        const [countrySlug, citySlug] = this.resolveSlugs(target);
        const typeSlug = CITY_FEATURE_TYPE_ROUTE_PATHS[type];
        const typeHasItsOwnPage = CITY_FEATURE_PAGE_PER_ITEM_TYPES.includes(type);

        if (typeHasItsOwnPage) {
            const hash = target.section ? `#${target.section}` : '';
            return {
                href: `${countrySlug}/${citySlug}/${typeSlug}/${featureSlug}${hash}`,
                routeId: `/[countrySlug]/[citySlug]/[${typeSlug}]/[featureSlug]`
            };
        } else {
            return {
                href: `${countrySlug}/${citySlug}/${typeSlug}#${featureSlug}`,
                routeId: `/[countrySlug]/[citySlug]/[${typeSlug}]`
            };
        }
    }

    public static toCountries({ query }: { query?: string } = {}): NavLink {
        if (query) {
            const params = new URLSearchParams();
            params.set('q', query)
            return { href: `countries?${params.toString()}`, routeId: '/countries' };
        } else {
            return { href: `countries`, routeId: '/countries' };
        }
    }

    public static toCities({ page = 1, filters = { ...defaultCityFilters } }: { page?: number; filters?: CityFilters } = {}): NavLink {
        const params = new URLSearchParams();
        if (!UrlUtil.isFirstPage(page)) {
            params.set('page', String(page));
        }

        if (filters.q) params.set('q', filters.q);
        if (filters.cityscapes?.length) params.set('cityscapes', filters.cityscapes.join(','));
        if (filters.climates?.length) params.set('climates', filters.climates.join(','));
        if (filters.landscapes?.length) params.set('landscapes', filters.landscapes.join(','));
        if (filters.tags?.length) params.set('tags', filters.tags.join(','));

        const queryString = params.toString();
        const href = queryString ? `cities?${queryString}` : 'cities';

        return { href, routeId: '/cities', preloadData: 'tap' };
    }

    public static toRandomCity(): NavLink {
        return { href: `cities/random`, routeId: '/cities/random', preloadData: 'tap' };
    }

    public static toSearch({ query }: { query?: string } = {}): NavLink {
        if (query) {
            const params = new URLSearchParams();
            if (query) params.set('q', query);
            return { href: `search?${params.toString()}`, routeId: '/search', preloadData: 'tap' };
        } else {
            return { href: `search`, routeId: '/search', preloadData: 'tap' };
        }
    }

    public static toAtlas(): NavLink {
        return { href: `atlas`, routeId: '/atlas' };
    }

    public static toAll(): NavLink {
        return { href: `all`, routeId: '/all' };
    }

    public static toAllCollection({ slug, page = 1 }: { slug: string, page?: number }): NavLink {
        if (UrlUtil.isFirstPage(page)) {
            return { href: `all/${slug}`, routeId: '/all/[collectionSlug]' };
        } else {
            return { href: `all/${slug}/${page}`, routeId: '/all/[collectionSlug]/[[page]]' };
        }
    }

    public static toCountryCollection({ countrySlug, collectionSlug }: { countrySlug: string, collectionSlug: string }): NavLink {
        return { href: `${countrySlug}/${collectionSlug}`, routeId: '/[countrySlug]/[collectionSlug]' };
    }

    public static toAbout(): NavLink {
        return { href: `about`, routeId: '/about' };
    }

    public static toTermsOfUse(): NavLink {
        return { href: `terms-of-use`, routeId: '/terms-of-use' };
    }

    public static toPrivacyPolicy(): NavLink {
        return { href: `privacy-policy`, routeId: '/privacy-policy' };
    }

    public static getFullImageUrl(image: (CoverImage | string)): string {
        if (typeof image === 'string') {
            if (image.startsWith(IMAGE_BASE_URL)) {
                return image;
            }
            return `${IMAGE_BASE_URL}/${image}`;
        } else {
            return `${IMAGE_BASE_URL}/${image.filename}`;
        }
    }

    /**
     * Returns the full URL of a static file
     * @param type which type of file we're requesting
     * @param filename filename of the file we're requesting, including extension
     */
    public static getStaticFileFullUrl(type: 'images', filename: string): string {
        return `${FULL_SITE_URL}/${type}/${filename}`;
    }

    // --------- UTILS ---------

    public static isActive({ currentRouteId, link }: { currentRouteId: string | null, link: NavLink | string }): boolean {
        if (currentRouteId === null) return false;
        const targetRouteId: string = typeof link === 'string' ? link : link.routeId;
        return currentRouteId.toLowerCase() === targetRouteId.toLowerCase();
    }

    private static isFirstPage(page: number): boolean {
        return page <= 1;
    }

    private static resolveSlugs(target: CityLocationTarget): [string, string] {
        return target.location
            ? [target.location.country.slug, target.location.city.slug]
            : [target.countrySlug, target.citySlug];
    }

}